import { useAuth, INACTIVITY_MINS, TIMEOUT_WARNING_MINS } from "common/hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import format from "date-fns/format";
import styled from "styled-components";
import { emitter } from "common/emitter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-light-svg-icons";

import {
    captureBreadcrumb,
    captureException
} from "extensions/sentry";
import { Redirect } from "extensions/react-router";

import { PrimaryButton, SecondaryButton } from "common/components/buttons";
import { Modal } from "common/components/modal";
import { log } from "common/components/debug";

import { RETURN_TO_CURRENT_URL_AFTER_LOGIN, REFRESH_TOKEN } from "common/actions/authentication";
import { SHOW_SESSION_TIMEOUT, HIDE_SESSION_TIMEOUT, INTERACT } from "common/reducers/authentication";

export const SessionTimeoutModal = function() {
    const dispatch = useDispatch();
    const last_interacted = useSelector((state) => state.authentication.last_interacted);
    const { isAuthenticated, sessionTimeoutModalShown } = useAuth({ trackInteraction: true });
    const modalTimer = useRef();
    const logoutTimer = useRef();
    const [extendingInProgress, setExtendingInProgress] = useState(false);

    const [shouldRedirectToLogout, setShouldRedirectToLogout] = useState(false);

    // Redirect once, and once only.
    useEffect(() => { if (shouldRedirectToLogout) setShouldRedirectToLogout(false); }, [shouldRedirectToLogout]);
    
    // PCI policy says we should logout a user after 15 minutes of inactivity
    const kickoutAt = last_interacted + INACTIVITY_MINS * 60 * 1000;
    
    log("@@@ Auth rerender");
    // Remove any visible AppCues tips on visible dialog
    useEffect(() => {
        try {
            if (sessionTimeoutModalShown) {
                // Stop polling while the timeout modal is shown to prevent polling requests from refreshing access token.
                emitter.emit("polling:stop");
                document.querySelectorAll("appcues-layer").forEach((e) => e.remove());
            } else {
                // Once the modal is no longer showing, it is fine to resume the polling.
                emitter.emit("polling:reset");
            }
        } catch (error) {
            captureException(error);
        }
    }, [sessionTimeoutModalShown]);
    
    // Add blurred effect when the dialog is shown
    useEffect(() => {
        if (sessionTimeoutModalShown) {
            document.getElementById("wrapper").classList.add("blurred");
            return () => document.getElementById("wrapper").classList.remove("blurred");
        } else {
            document.getElementById("wrapper").classList.remove("blurred");
        }
    }, [sessionTimeoutModalShown]);
    
    // Schedule session timeout dialog
    useEffect(() => {
        const showSessionTimeoutTimerAt = kickoutAt - TIMEOUT_WARNING_MINS * 60 * 1000;
        
        if (sessionTimeoutModalShown) {
            if (showSessionTimeoutTimerAt > Date.now()) {
                dispatch(HIDE_SESSION_TIMEOUT());
            } else {
                // Return earlier if session modal is currently shown
                return;
            }
        }
        
        const clearDialogTimer = () => {
            if (modalTimer.current) {
                log("@@@ Auth Clear dialog timer");
                clearTimeout(modalTimer.current);
            }
        };
        const scheduleDialogTimer = () => {
            clearDialogTimer();
            
            // Schedule Session Timeout to show
            modalTimer.current = setTimeout(() => {
                captureBreadcrumb({
                    message: "Showing session warning timeout",
                    category: "security"
                });
                dispatch(SHOW_SESSION_TIMEOUT());
            }, kickoutAt - TIMEOUT_WARNING_MINS * 60 * 1000 - Date.now()); // Show timeout modal two TIMEOUT_WARNING_MINS before kicking out
    
            log(`@@@ Auth Scheduled timeout warning timer in ${format(Math.abs(kickoutAt - TIMEOUT_WARNING_MINS * 60 * 1000 - Date.now()), "mm:ss")}`);
        };
        
        const clearLogoutTimer = () => {
            if (logoutTimer.current) {
                log("@@@ Auth Clear logout timer");
                clearTimeout(logoutTimer.current);
            }
        };
        const scheduleLogoutTimer = () => {
            clearLogoutTimer();
    
            // Schedule automatic kick out
            logoutTimer.current = setTimeout(() => {
                captureBreadcrumb({
                    message: "Automatic logout after inactivity",
                    category: "security"
                });
                log("@@@ Auth redirect to logout");
    
                emitter.emit("logout:before");
                dispatch(RETURN_TO_CURRENT_URL_AFTER_LOGIN());
                setShouldRedirectToLogout(true);
            }, kickoutAt - Date.now());
            
            log(`@@@ Auth Scheduled logout timer in ${format(Math.abs(kickoutAt - Date.now()), "mm:ss")}`);
        };
        
        if (isAuthenticated()) {
            scheduleDialogTimer();
            scheduleLogoutTimer();
        } else {
            clearDialogTimer();
            clearLogoutTimer();
        }
    }, [dispatch, isAuthenticated, kickoutAt, sessionTimeoutModalShown]);

    if (!sessionTimeoutModalShown) return null;
    
    const extendSession = () => {
        setExtendingInProgress(true);
        // TODO: Add loading indicator to the extend session button
        dispatch(REFRESH_TOKEN())
            .then(() => {
                dispatch(INTERACT());
                dispatch(HIDE_SESSION_TIMEOUT());
                setExtendingInProgress(false);
            });
    };
    
    return shouldRedirectToLogout ? <Redirect push to="/merchant/logout?timeout=true" /> : (
        <Modal
            actions={[
                <SecondaryButton onClick={() => setShouldRedirectToLogout(true)}>Sign out</SecondaryButton>,
                <PrimaryButton onClick={extendSession}>
                    {extendingInProgress ? <FontAwesomeIcon icon={faSpinner} spin style={{ margin: "0 51" }}/> : "Extend my session"}
                </PrimaryButton>
            ]}
            className=""
            closeOnEsc={false}
            closeOnOverlayClick={false}
            header={<SessionModalHeader>Your session will time out in two minutes</SessionModalHeader>}
            portalId="modal-session-timeout"
            root={document.body}
            show={sessionTimeoutModalShown && isAuthenticated()}
            showCloseIcon={false}
            $zIndex={1032} // Must appear over export modal and packaged reports
        >
            <>
                <SessionCountdown expiresAt={kickoutAt} />
                <SessionModalMessage>For security purposes, we automatically sign you out after 15 minutes of inactivity.</SessionModalMessage>
                <SessionModalChoice>What would you like to do?</SessionModalChoice>
            </>
        </Modal>
    );
};

function SessionCountdown({ expiresAt }) {
    const countdownTimer = useRef();
    const [timeLeft, setTimeleft] = useState("");
    
    useEffect(() => {
        const updateCoundown = () => {
            setTimeleft(format(expiresAt - Date.now(), "mm:ss"));
            countdownTimer.current = setTimeout(updateCoundown, 1000);
        };
        
        updateCoundown();
        
        return () => {
            clearTimeout(countdownTimer.current);
        };
    }, [expiresAt]);
    
    return <Countdown>{expiresAt < Date.now() ? "Expired" : timeLeft}</Countdown>;
}

const SessionModalHeader = styled.div`
    text-align: center;
`;

const Countdown = styled.div`
    text-align: center;
    padding: 10px 0;
    font-family: ${({ theme }) => theme.text.medium};
    font-size: ${({ theme }) => theme.headerFontSizeLarge};
`;

const SessionModalMessage = styled.div`
    text-align: center;
    padding: 10px 0 20px;
    font-size: ${({ theme }) => theme.fontSizeLarge};
`;

const SessionModalChoice = styled.div`
    text-align: center;
    font-size: ${({ theme }) => theme.fontSizeExtraLarge};
`;
